import type {
  CalendarEvent,
  EnrollmentPayment,
  Olympiad,
  OlympiadPhase,
  Student,
} from "./types";
import { compareClassNames, formatClassName } from "./classCode";

export interface PhaseDateRange {
  date: string;
  endDate: string;
}

export function isPhaseWindow(phase: OlympiadPhase) {
  return (
    phase.dateType === "window" ||
    (!phase.dateType && Boolean(phase.endDate && phase.endDate !== phase.date))
  );
}

const positive = (value: unknown) => {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

export function isPaidOlympiad(olympiad: Olympiad) {
  if (olympiad.studentFeeEnabled !== undefined)
    return olympiad.studentFeeEnabled;
  return olympiad.costType === "paid" || positive(olympiad.cost) > 0 || olympiad.feeVariationByPhase === true || olympiad.feeVariationByClass === true;
}

export function isStudentEligible(olympiad: Olympiad, student: Student) {
  if (
    !student.active ||
    olympiad.allowed === false
  )
    return false;
  const classes = olympiad.eligibleClasses ?? [];
  if (classes.length > 0) return classes.includes(student.className);
  return olympiad.eligibleGrades.includes(student.grade);
}

export function canStudentEnroll(olympiad: Olympiad, student: Student) {
  return olympiad.enrollmentEnabled !== false && isStudentEligible(olympiad, student);
}

export function isOlympiadVisibleToStudent(
  olympiad: Olympiad,
  student: Student,
) {
  return olympiad.published && isStudentEligible(olympiad, student);
}

export function scopeOlympiadToStudent(
  olympiad: Olympiad,
  student: Student,
): Olympiad {
  const firstPhase = olympiad.phases[0];
  const studentCost = firstPhase
    ? resolvePhaseFee(olympiad, student, firstPhase)
    : positive(olympiad.cost);
  const studentExamDate = firstPhase
    ? getPhaseDateRange(firstPhase, student.className).date
    : olympiad.examDate;
  return {
    ...olympiad,
    cost: studentCost,
    examDate: studentExamDate || olympiad.examDate,
    eligibleClasses: olympiad.eligibleClasses?.length
      ? [student.className]
      : olympiad.eligibleClasses,
    classFees: olympiad.classFees?.[student.className] !== undefined
      ? { [student.className]: olympiad.classFees[student.className] }
      : {},
    classPhaseFees: olympiad.classPhaseFees?.[student.className]
      ? { [student.className]: olympiad.classPhaseFees[student.className] }
      : {},
    phases: olympiad.phases.map((phase) => ({
      ...phase,
      classDates: phase.classDates?.[student.className]
        ? { [student.className]: phase.classDates[student.className] }
        : {},
    })),
  };
}

export function resolvePhaseFee(
  olympiad: Olympiad,
  student: Student,
  phase: OlympiadPhase,
) {
  if (!isPaidOlympiad(olympiad)) return 0;
  const classPhaseFee = positive(
    olympiad.classPhaseFees?.[student.className]?.[phase.id],
  );
  if (
    olympiad.feeVariationByClass &&
    olympiad.feeVariationByPhase &&
    classPhaseFee
  )
    return classPhaseFee;
  const phaseFee = positive(phase.feeAmount);
  if (olympiad.feeVariationByPhase && phaseFee) return phaseFee;
  const classFee = positive(olympiad.classFees?.[student.className]);
  if (olympiad.feeVariationByClass && classFee) return classFee;
  return positive(olympiad.cost);
}

export function buildInitialPayments(
  olympiad: Olympiad,
  student: Student,
): EnrollmentPayment[] {
  if (!isPaidOlympiad(olympiad)) return [];
  const firstPhase = olympiad.phases[0] ?? {
    id: `${olympiad.id}-fase-1`,
    title: "Inscrição",
    date: olympiad.enrollmentEnd,
    status: "next" as const,
  };
  return [
    {
      id: crypto.randomUUID(),
      phaseId: firstPhase.id,
      phaseTitle: firstPhase.title,
      amount: resolvePhaseFee(olympiad, student, firstPhase),
      scope: olympiad.format === "Equipe" ? "team" : "student",
      status: "pending",
      dueDate: olympiad.enrollmentEnd,
    },
  ];
}

export function canConfirmEnrollment(
  payments: EnrollmentPayment[] | undefined,
) {
  return (
    !payments?.length || payments.every((payment) => payment.status === "paid")
  );
}

export function getPhaseDateRange(
  phase: OlympiadPhase,
  className?: string,
): PhaseDateRange {
  const usesClassDate = Boolean(phase.datesVaryByClass && className);
  const classDate = usesClassDate ? phase.classDates?.[className!] : undefined;
  const date = usesClassDate ? classDate?.date ?? "" : phase.date ?? "";
  const endDate =
    isPhaseWindow(phase)
      ? usesClassDate
        ? classDate?.endDate ?? ""
        : phase.endDate ?? ""
      : date;
  return { date, endDate };
}

export function formatPhaseDate(
  phase: OlympiadPhase,
  className?: string,
): string {
  const { date, endDate } = getPhaseDateRange(phase, className);
  if (!date) return "Data não informada";
  const format = (value: string) => value.split("-").reverse().join("/");
  return isPhaseWindow(phase) && endDate !== date
    ? `${format(date)} a ${format(endDate)}`
    : format(date);
}

export function buildOlympiadPhaseEvents(
  olympiads: Olympiad[],
  className?: string,
  includeAllClasses = false,
): CalendarEvent[] {
  return olympiads.flatMap((olympiad) =>
    olympiad.phases.flatMap((phase) => {
      const scopes = phase.datesVaryByClass
        ? includeAllClasses
          ? [...(olympiad.eligibleClasses ?? [])].sort(compareClassNames)
          : className
            ? [className]
            : []
        : [""];
      return scopes.flatMap((scope) => {
        const range = getPhaseDateRange(phase, scope || undefined);
        if (!range.date) return [];
        const suffix = scope ? ` · ${formatClassName(scope)}` : "";
        const base = {
          unitId: olympiad.unitId,
          time: phase.time,
          type: "exam" as const,
          olympiadId: olympiad.id,
        };
        const start: CalendarEvent = {
          ...base,
          id: `phase-${olympiad.id}-${phase.id}-${scope || "all"}-start`,
          date: range.date,
          title: `${isPhaseWindow(phase) ? "Início da janela" : phase.title}: ${olympiad.acronym}${suffix}`,
        };
        if (!isPhaseWindow(phase) || !range.endDate || range.endDate === range.date)
          return [start];
        return [
          start,
          {
            ...base,
            id: `phase-${olympiad.id}-${phase.id}-${scope || "all"}-end`,
            date: range.endDate,
            title: `Fim da janela: ${olympiad.acronym} · ${phase.title}${suffix}`,
          },
        ];
      });
    }),
  );
}

export function validatePhaseContinuity(
  phases: OlympiadPhase[],
  classNames: string[],
): string[] {
  const errors: string[] = [];
  const scopes = classNames.length ? classNames : [""];

  phases.forEach((phase, index) => {
    const phaseLabel = phase.title.trim() || `Fase ${index + 1}`;
    if (!phase.title.trim()) errors.push(`Informe o nome da fase ${index + 1}.`);
    if (phase.datesVaryByClass && !classNames.length)
      errors.push(`${phaseLabel}: selecione as turmas antes de variar as datas.`);

    scopes.forEach((className) => {
      const scopeLabel = className ? ` para a turma ${formatClassName(className)}` : "";
      const range = getPhaseDateRange(phase, className || undefined);
      if (!range.date) {
        errors.push(`${phaseLabel}: informe a data${scopeLabel}.`);
        return;
      }
      if (isPhaseWindow(phase) && !range.endDate)
        errors.push(`${phaseLabel}: informe o fim da janela${scopeLabel}.`);
      else if (range.endDate < range.date)
        errors.push(`${phaseLabel}: o fim da janela deve ser igual ou posterior ao início${scopeLabel}.`);

      if (index > 0) {
        const previous = getPhaseDateRange(phases[index - 1], className || undefined);
        if (previous.endDate && range.date < previous.endDate)
          errors.push(
            `${phaseLabel}: a etapa deve começar após o término da fase anterior${scopeLabel}.`,
          );
      }
    });
  });

  return [...new Set(errors)];
}
